import { createMentionLinkNode } from './create'

type LineContentPart =
  | { type: 'text'; value: string }
  | {
      type: 'mention'
      name: string
      userId: string
    }

const UUID_LENGTH = 36
const MENTION_REGEX = new RegExp(
  /\[.+?]\(mention:\/\/user_id\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/,
  'g',
)

function getMentionLinkText(userId: string, name: string) {
  return `[${name}](mention://user_id/${userId})`
}

export function convertHtmlToFormattedText(node: Node, isEditorLevel = true) {
  const { nodeName, textContent } = node

  if (nodeName === 'BR') return '\n'
  if (nodeName === '#text' || nodeName === 'SPAN') return textContent
  if (nodeName === 'A') {
    const { userId } = (node as HTMLAnchorElement).dataset
    return getMentionLinkText(userId || '', textContent || '')
  }

  if (nodeName !== 'DIV' && nodeName !== '#document-fragment') return ''

  const { childNodes } = node
  let formattedText = ''

  for (let i = 0; i < childNodes.length; i += 1) {
    const childNode = childNodes.item(i)

    formattedText += convertHtmlToFormattedText(childNode, false)
  }

  if (nodeName === 'DIV' && !isEditorLevel) {
    if (node.previousSibling?.nodeName !== 'DIV')
      formattedText = `\n${formattedText}`

    if (node.nextSibling) formattedText = `${formattedText}\n`
  }
  return formattedText
}

export function convertFormattedTextToHtml(text: string) {
  const lines = text.split('\n')
  const lineElements = lines.map((line, lineIndex) => {
    const lineContentFragment = document.createDocumentFragment()

    const matches = []
    while (true) {
      const match = MENTION_REGEX.exec(line)
      if (match) matches.push({ value: match[0], index: match.index })
      else break
    }

    const contentParts: LineContentPart[] = []
    let remainingContentStartIndex = 0

    matches.forEach(({ value, index }) => {
      const textBeforeMention = line.slice(remainingContentStartIndex, index)
      const mentionPart = value

      if (textBeforeMention)
        contentParts.push({ type: 'text', value: textBeforeMention })

      if (mentionPart) {
        const name =
          mentionPart.match(new RegExp(/\[.+?]/))?.[0]?.slice(1, -1) || ''
        const userId = mentionPart.slice(-UUID_LENGTH - 1, -1)

        contentParts.push({ type: 'mention', name, userId })
      }

      remainingContentStartIndex = index + value.length
    })

    const lastTextContentPart = line.slice(remainingContentStartIndex)
    if (lastTextContentPart)
      contentParts.push({ type: 'text', value: lastTextContentPart })

    contentParts.forEach(part =>
      lineContentFragment.appendChild(
        part.type === 'text'
          ? document.createTextNode(part.value)
          : createMentionLinkNode(part.name, part.userId),
      ),
    )

    if (lineIndex < lines.length - 1)
      lineContentFragment.appendChild(document.createElement('br'))

    return lineContentFragment
  })

  const fragment = document.createDocumentFragment()
  fragment.append(...lineElements)
  return fragment
}
